import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { API_BASE, s, authHeaders, fmtTime } from './sharedStyles';

const POLL_INTERVAL = 10000;
const EVENT_TYPES = [
  'TICKET_JOINED', 'TICKET_CALLED', 'TICKET_SKIPPED',
  'TICKET_FINISHED', 'TICKET_CANCELLED', 'TICKET_GRACE_EXPIRED', 'TICKET_EOD_CLOSED',
];

export default function DeadLetterPage() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [page, setPage] = useState(0);
  const [eventType, setEventType] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', '50');
    if (eventType) params.set('eventType', eventType);
    if (includeResolved) params.set('includeResolved', 'true');
    return params.toString();
  }, [page, eventType, includeResolved]);

  const fetchList = useCallback(() => {
    fetch(`${API_BASE}/api/v1/monitor/dead-letter?${query}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((data) => {
        setRows(data.content || []);
        setTotalElements(data.totalElements || 0);
        setUnresolvedCount(data.unresolvedCount || 0);
        setError(null);
      })
      .catch(() => setError('Failed to load dead-letter events'));
  }, [token, query]);

  useEffect(() => {
    fetchList();
    const id = setInterval(fetchList, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchList]);

  const loadDetail = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    fetch(`${API_BASE}/api/v1/monitor/dead-letter/${id}`, { headers: authHeaders(token) })
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then(setDetail)
      .catch(() => setError('Failed to load event detail'));
  };

  const act = (id, action) => {
    if (action === 'discard' && !window.confirm('Discard this event permanently?')) return;
    setBusy(true);
    fetch(`${API_BASE}/api/v1/monitor/dead-letter/${id}/${action}`, {
      method: 'POST',
      headers: authHeaders(token),
    })
      .then((r) => r.ok ? r.json() : r.json().then((b) => Promise.reject(b)))
      .then(() => {
        fetchList();
        if (expandedId === id) loadDetail(id);
      })
      .catch((b) => setError(b?.message || 'Action failed'))
      .finally(() => setBusy(false));
  };

  return (
    <div>
      <main style={s.main}>
        <h1 style={s.pageTitle}>Dead-Letter Queue</h1>

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ ...s.card, flex: 1, marginBottom: 0 }}>
            <div style={s.cardTitle}>Unresolved</div>
            <div style={{ ...s.metricBig, color: unresolvedCount > 0 ? '#EF4444' : '#10B981' }}>{unresolvedCount}</div>
          </div>
          <div style={{ ...s.card, flex: 1, marginBottom: 0 }}>
            <div style={s.cardTitle}>Total (all time)</div>
            <div style={s.metricBig}>{totalElements}</div>
          </div>
        </div>

        <div style={s.filterBar}>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Event Type</label>
            <select
              style={s.filterSelect}
              value={eventType}
              onChange={(e) => { setEventType(e.target.value); setPage(0); }}
            >
              <option value="">All</option>
              {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Resolved</label>
            <label style={{ color: '#CBD5E1', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(e) => { setIncludeResolved(e.target.checked); setPage(0); }}
              />
              Include resolved
            </label>
          </div>
          <button style={s.outlineBtn} onClick={fetchList} disabled={busy}>Refresh</button>
        </div>

        {error && <div style={{ ...s.card, borderColor: '#EF4444', color: '#EF4444' }}>{error}</div>}

        <div style={s.card}>
          {rows.length === 0 ? (
            <div style={s.detail}>No events to display.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ color: '#94A3B8', textAlign: 'left' }}>
                  <th style={th}>Parked</th>
                  <th style={th}>Event</th>
                  <th style={th}>Error</th>
                  <th style={th}>Attempts</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <>
                    <tr key={r.id} style={tr} onClick={() => loadDetail(r.id)}>
                      <td style={td}>{fmtTime(r.parkedAt)}</td>
                      <td style={{ ...td, color: '#F8FAFC', fontFamily: 'monospace' }}>{r.eventType}</td>
                      <td style={{ ...td, color: '#FCA5A5', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.error}</td>
                      <td style={td}>{r.attempts}</td>
                      <td style={td}>
                        {r.resolvedAt
                          ? <span style={{ color: r.resolution === 'REPLAYED' ? '#10B981' : '#94A3B8' }}>{r.resolution}</span>
                          : <span style={{ color: '#F59E0B' }}>pending</span>}
                      </td>
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        {!r.resolvedAt && (
                          <>
                            <button style={{ ...s.primaryBtn, padding: '4px 10px', fontSize: '0.75rem' }} disabled={busy} onClick={() => act(r.id, 'replay')}>Replay</button>
                            <button style={{ ...s.outlineBtn, padding: '4px 10px', fontSize: '0.75rem', marginLeft: 6, borderColor: '#EF4444', color: '#EF4444' }} disabled={busy} onClick={() => act(r.id, 'discard')}>Discard</button>
                          </>
                        )}
                      </td>
                    </tr>
                    {expandedId === r.id && (
                      <tr>
                        <td colSpan={6} style={{ background: '#0F172A', padding: '0.75rem', borderBottom: '1px solid #334155' }}>
                          {detail ? (
                            <div>
                              <div style={s.detail}><strong>Stream ID:</strong> <code>{detail.streamId}</code></div>
                              <div style={s.detail}><strong>First seen:</strong> {fmtTime(detail.firstSeenAt)}</div>
                              <div style={s.detail}><strong>Error:</strong></div>
                              <pre style={pre}>{detail.error}</pre>
                              <div style={s.detail}><strong>Payload:</strong></div>
                              <pre style={pre}>{prettify(detail.payload)}</pre>
                            </div>
                          ) : <div style={s.detail}>Loading...</div>}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
          <button style={s.outlineBtn} disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
          <span style={{ color: '#94A3B8', fontSize: '0.85rem', alignSelf: 'center' }}>Page {page + 1}</span>
          <button style={s.outlineBtn} disabled={(page + 1) * 50 >= totalElements} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      </main>
    </div>
  );
}

function prettify(payload) {
  if (!payload) return '';
  try {
    return JSON.stringify(typeof payload === 'string' ? JSON.parse(payload) : payload, null, 2);
  } catch {
    return String(payload);
  }
}

const th = { padding: '8px 10px', borderBottom: '1px solid #334155', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' };
const td = { padding: '8px 10px', borderBottom: '1px solid #1E293B', color: '#CBD5E1' };
const tr = { cursor: 'pointer' };
const pre = {
  background: '#020617', color: '#E2E8F0', padding: '0.75rem', borderRadius: 6,
  fontSize: '0.78rem', overflowX: 'auto', border: '1px solid #1E293B', marginTop: 4,
};
